"use client";

import React, { useState, useRef, useEffect } from "react";
import { Language } from "./types";
import useOutsideClick from "@/hooks/useOnClickOutside";
import { Icon } from "@ama-pt/agora-design-system";
import { useLocale, useTranslations } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import AgoraButton from "@/components/atoms/agoraButton";

interface LanguageSelectorProps {
  defaultLanguage?: string;
  languages?: Language[];
  onLanguageChange?: (language: Language) => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  defaultLanguage = "PT",
  languages = [
    { code: "PT", name: "Português", checked: true },
    { code: "EN", name: "English", checked: false },
  ],
  onLanguageChange,
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const locale = useLocale();

  const t = useTranslations("header");

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isChangingLanguage, setIsChangingLanguage] = useState<boolean>(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    languages.find((lang) => lang.code === locale.toUpperCase()) ||
      languages.find((lang) => lang.code === defaultLanguage) ||
      languages[0]
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const languageButtonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  useOutsideClick(panelRef as any, () => setIsOpen(false));

  const getTranslatedSlug = async (currentSlug: string, targetLocale: string): Promise<string> => {
    try {
      const response = await fetch(`/api/translate-slug?slug=${currentSlug}&locale=${targetLocale}&currentLocale=${locale}`);
      const data = await response.json();
      return data.translatedSlug || currentSlug;
    } catch (error) {
      console.error('Erro ao buscar slug traduzido:', error);
      return currentSlug;
    }
  };

  const handleLanguageChange = async (language: Language, newLocale: string) => {
    if (isChangingLanguage) return;
    
    setIsChangingLanguage(true);
    
    document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=${
      60 * 60 * 24 * 365
    }; SameSite=lax`;

    setSelectedLanguage(language);
    setIsOpen(false);

    if (onLanguageChange) {
      onLanguageChange(language);
    }
    
    toggleButtonRef.current?.focus();

    const pathSegments = pathname.split('/').filter(Boolean);
    
    if (pathSegments.length === 0) {
      router.replace('/', { locale: newLocale });
      setIsChangingLanguage(false);
      return;
    }
    
    const currentSlug = pathSegments[pathSegments.length - 1];
    
    try {
      const translatedSlug = await getTranslatedSlug(currentSlug, newLocale);
      if (pathSegments.length === 1) {
        router.replace(`/${translatedSlug}` as any, { locale: newLocale });
      } else {
        const newPathSegments = [...pathSegments];
        newPathSegments[newPathSegments.length - 1] = translatedSlug;
        const newPath = `/${newPathSegments.join('/')}`;
        router.replace(newPath as any, { locale: newLocale });
      }
    } catch (error) {
      console.error('Erro na tradução:', error);
      router.replace('/', { locale: newLocale });
    }
    
    setIsChangingLanguage(false);
  };

  useEffect(() => {
    const currentLang = languages.find(
      (lang) => lang.code.toLowerCase() === locale.toLowerCase()
    );
    if (currentLang && currentLang.code !== selectedLanguage.code) {
      setSelectedLanguage(currentLang);
    }
    if (isOpen) {
      setTimeout(() => {
        languageButtonsRef.current[0]?.focus();
      }, 10);
    }
  }, [locale, languages, selectedLanguage.code, isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === "Escape") {
      setIsOpen(false);
      toggleButtonRef.current?.focus();
    }
  };

  const handlePanelBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = event.relatedTarget as Node | null;

    if (isOpen && nextFocusedElement && !event.currentTarget.contains(nextFocusedElement)) {
      setIsOpen(false);
    }
  };

  const SwitchLang = (lang: Language) => {
    if (lang.code === selectedLanguage.code || isChangingLanguage) return;
    
    const newLocale = lang.code.toLowerCase();
    handleLanguageChange(lang, newLocale);
  };

  useEffect(() => {
    const currentLang = languages.find(
      (lang) => lang.code.toLowerCase() === locale
    );
    if (currentLang && currentLang.code !== selectedLanguage.code) {
      setSelectedLanguage(currentLang);
    }
  }, [locale, languages, selectedLanguage.code]);

  return (
    <div
      ref={panelRef}
      className="panel-menu languages-panel-menu"
      onKeyDown={handleKeyDown}
      onBlur={handlePanelBlur}
    >
      <AgoraButton
        appearance="link"
        ref={toggleButtonRef}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="panel-content-Languages"
        hasIcon
        leadingIcon={"agora-line-globe"}
        trailingIcon={
          isOpen ? "agora-line-chevron-up" : "agora-line-chevron-down"
        }
        className={`flex items-center justify-center agora-btn agora-btn-link-neutral ${
          isOpen ? "panel-menu-button has-panel expanded" : ""
        }`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isChangingLanguage}
      >
        <span className="children-wrapper hidden md:block">
          {isChangingLanguage ? "..." : selectedLanguage.name}
        </span>
        <span className="block md:hidden">
          {isChangingLanguage ? "..." : selectedLanguage.code}
        </span>
      </AgoraButton>

      {isOpen && (
        <div
          className="panel-menu-content !bg-[#F9F9FA]"
          style={{ position: "absolute", zIndex: 50 }}
          aria-label={`${t("languageSelector.listLang")}`}
        >
          <div
            className="panel-menu-content-children"
            id="panel-content-Languages"
          >
            <div
              className="languages-layout"
              role="listbox"
              aria-label={`${t("languageSelector.langOption")}`}
              tabIndex={-1}
            >
              {languages.map((lang, index) => (
                <div key={lang.code} className="language-grid-cell">
                  <button
                    ref={(el) => {
                      languageButtonsRef.current[index] = el;
                    }}
                    role="option"
                    aria-selected={lang.code === selectedLanguage.code}
                    className={`flex items-center justify-center agora-btn agora-btn-link-neutral agora-btn-with-icon language-wrapper ${
                      isChangingLanguage ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={() => SwitchLang(lang)}
                    disabled={isChangingLanguage}
                    aria-label={`${t("languageSelector.selectLang")} ${
                      lang.name
                    } ${lang.code}`}
                  >
                    <span className="children-wrapper">{lang.name}</span>
                    {lang.code === selectedLanguage.code && (
                      <span className="icon-wrapper trailing" aria-hidden="true">
                        <Icon name="agora-line-check" />
                      </span>
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
